import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Calendar, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { useGalleryViewer } from "../gallery/GalleryViewerProvider";
import { fallbackAlbums, fetchPublicGallery } from "../../utils/publicGalleryApi";

const GALLERY_PREVIEW_COUNT = 8;
const SWIPE_HINT_KEY = "smym-gallery-swipe-hint";
const HINT_MS = 4000;

function readSwipeHintDismissed() {
  try {
    return window.localStorage.getItem(SWIPE_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberSwipeHintDismissed() {
  try {
    window.localStorage.setItem(SWIPE_HINT_KEY, "1");
  } catch {
    /* private mode */
  }
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function slidePhotos(album) {
  const photos = (Array.isArray(album.photos) ? album.photos : []).filter(
    (photo) => photo && photo.url,
  );
  if (photos.length === 0) return [];
  const cover = album.cover?.url;
  if (!cover || cover === photos[0].url) return photos;
  return [{ ...photos[0], url: cover }, ...photos.slice(1)];
}

function sliderMetrics(track) {
  const slides = track.querySelectorAll("[data-photo-slide]");
  const width = track.clientWidth;
  if (!Number.isFinite(width) || width <= 0) {
    return { stride: 1, max: 0, count: slides.length, index: 0 };
  }
  const max = Math.max(0, track.scrollWidth - width);
  const index =
    max < 2 ? 0 : Math.min(slides.length - 1, Math.round(track.scrollLeft / width));
  return { stride: width, max, count: slides.length, index };
}

function AlbumPhotoSlider({ album, onOpen, hint = false }) {
  const trackRef = useRef(null);
  const slides = slidePhotos(album);
  const [index, setIndex] = useState(0);
  const [nudge, setNudge] = useState(false);
  const [hintPhase, setHintPhase] = useState(() =>
    hint && slides.length > 1 && !readSwipeHintDismissed() ? "show" : "off",
  );
  const count = slides.length;
  const hintOn = hintPhase !== "off";

  const dismissHint = useCallback(() => {
    if (!hint) return;
    rememberSwipeHintDismissed();
    setHintPhase((phase) => {
      if (phase === "off" || phase === "hide") return phase;
      return prefersReducedMotion() ? "off" : "hide";
    });
  }, [hint]);

  useEffect(() => {
    if (hintPhase !== "show") return undefined;
    const timer = window.setTimeout(dismissHint, HINT_MS);
    return () => window.clearTimeout(timer);
  }, [hintPhase, dismissHint]);

  useEffect(() => {
    if (hintPhase !== "hide") return undefined;
    const timer = window.setTimeout(() => setHintPhase("off"), 400);
    return () => window.clearTimeout(timer);
  }, [hintPhase]);

  useEffect(() => {
    if (hintOn && index > 0) dismissHint();
  }, [hintOn, index, dismissHint]);

  useEffect(() => {
    if (!hint || count < 2 || prefersReducedMotion() || readSwipeHintDismissed()) {
      return undefined;
    }
    const track = trackRef.current;
    if (!track || typeof IntersectionObserver !== "function") return undefined;
    let played = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (played || !entries.some((entry) => entry.isIntersecting)) return;
        played = true;
        observer.disconnect();
        setNudge(true);
      },
      { threshold: 0.6 },
    );
    observer.observe(track);
    return () => observer.disconnect();
  }, [hint, count]);

  const openAt = (shown) => {
    dismissHint();
    onOpen(shown);
  };

  const syncIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const metrics = sliderMetrics(track);
    setIndex((current) => (current === metrics.index ? current : metrics.index));
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    syncIndex();
    track.addEventListener("scroll", syncIndex, { passive: true });
    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(syncIndex) : null;
    observer?.observe(track);
    return () => {
      track.removeEventListener("scroll", syncIndex);
      observer?.disconnect();
    };
  }, [count, syncIndex]);

  const scrollToIndex = useCallback((nextIndex) => {
    const track = trackRef.current;
    if (!track) return;
    const metrics = sliderMetrics(track);
    const next = Math.max(0, Math.min(metrics.count - 1, nextIndex));
    const left = Math.max(0, Math.min(metrics.max, next * metrics.stride));
    const behavior = prefersReducedMotion() ? "auto" : "smooth";
    try {
      track.scrollTo({ left, behavior });
    } catch {
      track.scrollLeft = left;
    }
    setIndex(next);
  }, []);

  const openShown = (event) => {
    if (event.target.closest("button")) return;
    const track = trackRef.current;
    const shown = track ? sliderMetrics(track).index : index;
    openAt(shown);
  };

  const onTrackKeyDown = (event) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      scrollToIndex(index + (event.key === "ArrowRight" ? 1 : -1));
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openAt(index);
    }
  };

  if (count === 0) {
    return (
      <div className="relative flex aspect-[4/3] items-center justify-center bg-[var(--smym-paper-2)]">
        <Camera className="h-10 w-10 text-[var(--smym-line)]" aria-hidden />
      </div>
    );
  }

  return (
    <div
      className={`smym-photo-slider relative aspect-[4/3] overflow-hidden${
        nudge ? " smym-photo-slider--nudge" : ""
      }`}
      onAnimationEnd={(event) => {
        if (event.animationName === "smym-photo-nudge") setNudge(false);
      }}
    >
      <div
        ref={trackRef}
        className="smym-photo-slider__track absolute inset-0"
        role="region"
        aria-roledescription="carousel"
        aria-label={`${album.title} photos`}
        tabIndex={0}
        onKeyDown={onTrackKeyDown}
        onClick={openShown}
      >
        {slides.map((photo, photoIndex) => (
          <div
            key={photo.id || photoIndex}
            className="smym-photo-slider__slide"
            data-photo-slide
            aria-hidden={photoIndex === index ? undefined : true}
          >
            <button
              type="button"
              className="smym-photo-slider__open"
              tabIndex={photoIndex === index ? 0 : -1}
              aria-label={`Open ${album.title} photos, ${photoIndex + 1} of ${count}`}
              onClick={(event) => {
                event.stopPropagation();
                openAt(photoIndex);
              }}
            >
              <img
                src={photo.url}
                alt={photo.alt || album.title}
                className="h-full w-full object-cover transition duration-[220ms] group-hover:scale-[1.02]"
                loading={photoIndex === 0 ? "eager" : "lazy"}
                draggable="false"
              />
            </button>
          </div>
        ))}
      </div>
      <span className="pointer-events-none absolute right-2 top-2 z-[2] rounded-md bg-[color-mix(in_oklch,var(--smym-ink)_75%,transparent)] px-2 py-1 text-xs font-medium text-[oklch(0.98_0.01_78)]">
        {album.photoCount || count} photos
      </span>
      <span className="smym-photo-slider__expand" aria-hidden="true">
        <Expand className="h-3.5 w-3.5" />
      </span>
      {count > 1 && (
        <p
          className="smym-photo-slider__count"
          aria-live="polite"
          aria-atomic="true"
          aria-label={`Photo ${index + 1} of ${count} in ${album.title}`}
        >
          {index + 1} / {count}
        </p>
      )}
      {hintPhase !== "off" && (
        <p
          className={`smym-photo-slider__hint${
            hintPhase === "hide" ? " smym-photo-slider__hint--hide" : ""
          }`}
          aria-hidden="true"
        >
          Swipe · Tap to view
        </p>
      )}
      {count > 1 && index > 0 && (
        <button
          type="button"
          className="smym-photo-slider__arrow smym-photo-slider__arrow--prev"
          aria-label={`Previous photo in ${album.title}`}
          onClick={() => scrollToIndex(index - 1)}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
      )}
      {count > 1 && index < count - 1 && (
        <button
          type="button"
          className="smym-photo-slider__arrow smym-photo-slider__arrow--next"
          aria-label={`Next photo in ${album.title}`}
          onClick={() => scrollToIndex(index + 1)}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

const GallerySection = () => {
  const { openAlbum } = useGalleryViewer();
  const [albums, setAlbums] = useState(() => fallbackAlbums());
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancel = false;
    fetchPublicGallery().then((next) => {
      if (!cancel && next.length > 0) setAlbums(next);
    });
    return () => {
      cancel = true;
    };
  }, []);

  const visibleAlbums = showAll ? albums : albums.slice(0, GALLERY_PREVIEW_COUNT);

  return (
    <section
      id="gallery"
      className="smym-section border-t border-[var(--smym-line)] px-4 sm:px-6"
    >
      <div className="mx-auto max-w-6xl">
        <h2 className="smym-section-title mb-3 text-center">Gallery</h2>
        <p className="mx-auto mb-12 text-center text-[var(--smym-ink-muted)] [max-width:40ch]">
          AGMs, temple runs, Bulandi, and community programs
        </p>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleAlbums.map((album, albumIndex) => {
            const count = album.photoCount || album.photos?.length || 0;
            const hasPhotos = count > 0;

            return (
              <div
                key={album.id}
                className={`group overflow-hidden rounded-lg border border-[var(--smym-line)] bg-[var(--smym-paper)] text-left shadow-[var(--smym-shadow)] transition duration-[220ms] ${
                  hasPhotos ? "hover:-translate-y-0.5" : "opacity-70"
                }`}
              >
                <AlbumPhotoSlider
                  album={album}
                  hint={albumIndex === 0}
                  onOpen={(photoIndex) => hasPhotos && openAlbum(album, photoIndex)}
                />

                <div className="border-t border-[var(--smym-line)] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-semibold text-[var(--smym-ink)]">
                        {album.title}
                      </h4>
                      <p className="mt-1 text-xs font-medium text-[var(--smym-accent)]">
                        {album.category}
                      </p>
                    </div>
                    {album.date && (
                      <time
                        className="flex shrink-0 items-center gap-1 text-xs text-[var(--smym-ink-muted)]"
                        dateTime={album.date}
                      >
                        <Calendar className="h-3 w-3" aria-hidden />
                        {new Date(album.date).toLocaleDateString("en-IN", {
                          month: "short",
                          year: "numeric",
                        })}
                      </time>
                    )}
                  </div>
                  {album.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-[var(--smym-ink-muted)]">
                      {album.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {albums.length > GALLERY_PREVIEW_COUNT && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              aria-expanded={showAll}
              className="smym-btn smym-btn-outline"
            >
              {showAll ? "Show less" : "View more"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection;
